/**
 * Module-level singleton store for live CQG quotes. Populated by `useLiveQuotesListener()`
 * (mounted once in AppShell) and read by `useLiveQuote(symbol)` via useSyncExternalStore.
 *
 * We use a singleton + useSyncExternalStore (instead of React context) so individual rows
 * in the trades table re-render only when *their* symbol updates, not on every quote tick
 * for any symbol.
 */
export interface LiveQuote {
  last: number | null;
  bid:  number | null;
  ask:  number | null;
  ts:   string;
}

type Listener = () => void;

const quotes = new Map<string, LiveQuote>();
const allListeners = new Set<Listener>();                       // change-on-any-symbol
const symbolListeners = new Map<string, Set<Listener>>();        // change-on-this-symbol

export const liveQuotesStore = {
  set(symbol: string, q: Partial<LiveQuote> & { ts: string }) {
    const prev = quotes.get(symbol);
    const next: LiveQuote = {
      last: q.last ?? prev?.last ?? null,
      bid:  q.bid  ?? prev?.bid  ?? null,
      ask:  q.ask  ?? prev?.ask  ?? null,
      ts:   q.ts,
    };
    quotes.set(symbol, next);
    symbolListeners.get(symbol)?.forEach(l => l());
    allListeners.forEach(l => l());
  },
  get(symbol: string): LiveQuote | undefined {
    return quotes.get(symbol);
  },
  subscribeSymbol(symbol: string, listener: Listener): () => void {
    let set = symbolListeners.get(symbol);
    if (!set) { set = new Set(); symbolListeners.set(symbol, set); }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) symbolListeners.delete(symbol);
    };
  },
  subscribeAll(listener: Listener): () => void {
    allListeners.add(listener);
    return () => { allListeners.delete(listener); };
  },
  /**
   * Drop all cached quotes and notify every listener so rows re-render to "—". Called on CQG
   * disconnect: a frozen last price after the feed is gone is worse than showing nothing. Fresh
   * quotes repopulate the map on reconnect.
   */
  clear(): void {
    quotes.clear();
    symbolListeners.forEach(set => set.forEach(l => l()));
    allListeners.forEach(l => l());
  },
};
